import { useCallback, useEffect, useState } from "react";
import styled from "styled-components";
import TopPageTitle from "../../../components/TopPageTitle/TopPageTitle";
import { PagesWrapper } from "../../../styles/components/PagesWrapper.styled";
import axios from "../../../axios";
import * as S from "../Funnel/Funnel.styled";

// «Ризик відтоку»: хто з активних учнів може скоро піти і чому, хто на паузі / пішов і коли їм написати,
// + готовий текст для Лери. Дані: GET /admin/churn-risk. Правила — backend/controllers/churnController.js.

const LEVELS = [
  { id: "red", n: "🔴 високий ризик", hint: "2+ сигнали - написати цього тижня" },
  { id: "yellow", n: "🟡 під загрозою", hint: "1 сигнал - тримати в полі зору" },
  { id: "gone", n: "Фактично пішли", hint: "3+ тижні без уроків і баланс 0, але не на паузі - познач «пішов»" },
  { id: "ok", n: "✅ усе добре", hint: "сигналів немає" },
  { id: "paused", n: "⏸ на паузі", hint: "через 30 днів після паузи - нагадування написати" },
  { id: "left", n: "👋 пішли", hint: "через 60 днів після відходу - спробувати повернути" },
];
const PAUSE_LEVELS = ["paused", "left"];
// 6 груп: 3×2 на десктопі, 2 колонки на вузьких екранах
const Groups = styled(S.Steps)`
  grid-template-columns: repeat(3, 1fr);
  @media (max-width: 800px) { grid-template-columns: repeat(2, 1fr); }
`;
const OUTREACH = { contacted: ["написали", "violet"], replied: ["відповів", "amber"], renewed: ["продовжив ✓", "green"] };

// Шаблони для Лери: в особистих повідомленнях школа звертається на «ви», без довгих тире. Тип — за першим сигналом учня.
const TEMPLATES = {
  comeback: (n) => `Вітаю, ${n}! 👋 Це Лера зі Speaksy. Давно не бачилися на уроках - як ваша англійська? 🙂 Якщо захочете повернутися, підберемо зручний час і формат, щоб навчання знову йшло легко.`,
  pace: (n) => `Вітаю, ${n}! 👋 Це Лера зі Speaksy. Помітили, що останнім часом уроків стало менше - усе гаразд? Якщо розклад зараз незручний, можемо змінити час або частоту. Як вам навчання загалом? 🙂`,
  skips: (n) => `Вітаю, ${n}! 😊 Це Лера зі Speaksy. Бачу, що останні уроки часто переносилися - розумію, буває багато справ. Давайте підберемо час, який точно буде зручним? Напишіть, коли вам найкраще 🙌`,
  renewal: (n, bal) => (bal > 0
    ? `Вітаю, ${n}! 👋 Це Лера зі Speaksy. У вас залишилося ${bal} ${bal === 1 ? "урок" : "уроки"} - щоб не було перерви в навчанні, можемо вже зараз продовжити пакет. Підказати варіанти? 🙂`
    : `Вітаю, ${n}! 👋 Це Лера зі Speaksy. Ваш пакет уроків закінчився - продовжуємо? 🙂 Підкажу варіанти пакетів, щоб навчання йшло без перерви.`),
  paused: (n) => `Вітаю, ${n}! 👋 Це Лера зі Speaksy. Як ваші справи? Хотіла дізнатися, чи готові ви повернутися до англійської 🙂 Підберемо зручний час і викладача - усе під вас.`,
  left: (n) => `Вітаю, ${n}! 👋 Це Лера зі Speaksy. Давно не спілкувалися - як ваша англійська? 🙂 Якщо захочете повернутися до занять, будемо раді. Підберемо формат і час, які вам зараз підходять.`,
};
const KIND_ORDER = ["left", "paused", "comeback", "pace", "skips", "renewal"];
const firstName = (name) => String(name || "").trim().split(/\s+/)[0];
const messageFor = (s) => {
  const kind = KIND_ORDER.find((k) => s.signals.some((x) => x.kind === k)) || "pace";
  return TEMPLATES[kind](firstName(s.name), s.balance);
};

const day = (d) => (d ? new Date(d).toLocaleDateString("uk-UA", { day: "numeric", month: "short" }) : "—");
const withId = (name, id) => (id ? `${name} (ID ${id})` : name || "—");
const copy = (text) => navigator.clipboard?.writeText(text).then(() => true).catch(() => false);

function ChurnRisk() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [open, setOpen] = useState("red");
  const [dueOnly, setDueOnly] = useState(true);
  const [flash, setFlash] = useState("");

  const load = useCallback(() => {
    setError("");
    axios.get("admin/churn-risk")
      .then((res) => setData(res.data))
      .catch(() => setError("Не вдалося завантажити ризик відтоку"));
  }, []);
  useEffect(() => { load(); }, [load]);

  const notify = (text) => { setFlash(text); setTimeout(() => setFlash(""), 2500); };
  const isPauseView = PAUSE_LEVELS.includes(open);
  const shown = data ? data.students.filter((s) => s.level === open && (!isPauseView || !dueOnly || s.due)) : [];

  const copyList = async () => {
    const text = shown.map((s) => [
      `${withId(s.name, s.studentId)} · ${s.phoneNumber || "без телефону"} · викладач ${s.teacherName}`,
      `Чому: ${s.signals.map((x) => x.text).join("; ")}${s.pauseNote ? ` (${s.pauseNote})` : ""}`,
      `Текст: ${messageFor(s)}`,
    ].join("\n")).join("\n\n");
    notify((await copy(text)) ? `Скопійовано ${shown.length} учнів зі шаблонами - можна переслати Лері` : "Не вдалося скопіювати");
  };

  return (
    <PagesWrapper>
      <TopPageTitle value={"Ризик відтоку"} />
      <S.Page>
        {error && <S.ErrorText>{error}</S.ErrorText>}
        {data && (
          <>
            <S.Card>
              <S.CardHead>
                <div>
                  <S.H>Хто може скоро піти</S.H>
                  <S.Hint>Без тестових акаунтів. Рахується щоразу заново, станом на {day(data.generatedAt)} Натисни на цифру, щоб побачити, хто в ній і чому.</S.Hint>
                </div>
              </S.CardHead>
              <Groups>
                {LEVELS.map((l) => (
                  <S.Step key={l.id} as="button" type="button" onClick={() => setOpen(l.id)} $active={open === l.id}>
                    <S.Big>{data.counts[l.id]}</S.Big><S.Small><b>{l.n}</b></S.Small><S.Small>{l.hint}</S.Small>
                    {l.id === "paused" && data.counts.due > 0 && <S.Rate>пора написати: {data.counts.due}</S.Rate>}
                  </S.Step>
                ))}
              </Groups>
              <S.Hint style={{ marginTop: 10 }}>
                За 30 днів написали {data.outreach30d.contacted} учням, з них продовжили {data.outreach30d.renewed}. Пішли за 30 днів: {data.churn.left30d}.
                {data.churn.reasons90d.length > 0 && ` Причини (90 днів): ${data.churn.reasons90d.map((r) => `${r.reason} ${r.n}`).join(", ")}.`}
              </S.Hint>
            </S.Card>

            <S.Card>
              <S.CardHead>
                <div>
                  <S.H>{LEVELS.find((l) => l.id === open).n} ({shown.length})</S.H>
                  <S.Hint>«Текст» копіює готове повідомлення під причину. «Список для Лери» - усі учні з цієї групи разом із текстами.</S.Hint>
                </div>
                <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                  {isPauseView && (
                    <label style={{ display: "flex", gap: 6, alignItems: "center", fontSize: 14, cursor: "pointer" }}>
                      <input type="checkbox" style={{ appearance: "auto", WebkitAppearance: "auto" }} checked={dueOnly} onChange={(e) => setDueOnly(e.target.checked)} /> лише кому пора написати
                    </label>
                  )}
                  {open !== "ok" && shown.length > 0 && <S.Btn onClick={copyList}>Список для Лери</S.Btn>}
                </div>
              </S.CardHead>
              {flash && <S.Hint style={{ color: "#15803d", fontWeight: 700, marginBottom: 8 }}>{flash}</S.Hint>}
              {shown.length === 0 ? <S.Empty>{isPauseView && dueOnly ? "Зараз нікому не пора писати 🙂" : "Нікого - і це добре 🙂"}</S.Empty> : (
                <S.Table>
                  <table>
                    {isPauseView
                      ? <thead><tr><th>Учень</th><th>Викладач</th><th>{open === "left" ? "Пішов" : "На паузі з"}</th><th>Причина / нотатка</th><th>Написати після</th><th>Контакт</th></tr></thead>
                      : <thead><tr><th>Учень</th><th>Викладач</th><th>Баланс</th><th>Останній урок</th><th>За 4 тиж.</th><th>Чому в списку</th><th>Контакт</th></tr></thead>}
                    <tbody>
                      {shown.map((s) => (isPauseView
                        ? <PausedRow key={s._id} s={s} reasons={data.leftReasons} onChange={load} notify={notify} />
                        : <Row key={s._id} s={s} reasons={data.leftReasons} onChange={load} notify={notify} />))}
                    </tbody>
                  </table>
                </S.Table>
              )}
            </S.Card>

            <S.Card>
              <S.H>Як рахується</S.H>
              <S.List style={{ marginTop: 8 }}>
                <li><span>14+ днів без відмічених уроків</span></li>
                <li><span>За 4 тижні уроків менше ніж половина від розкладу</span></li>
                <li><span>З дати останньої оплати використано менше 60% уроків, які мали пройти за розкладом</span></li>
                <li><span>Прогул (урок відмічено «учень не прийшов») · 2+ скасування за 4 тижні</span></li>
                <li><span>Залишилось 2 уроки або менше</span></li>
              </S.List>
              <S.Hint style={{ marginTop: 8 }}>
                «Перевір з викладачем» - уроків менше, ніж за розкладом, а скасувань і прогулів немає: або учень зникає, або викладач не відмічає уроки.
                Пауза і «пішов» ставляться в картці учня або тут, у рядку («Позначити… → пішов»). Старі паузи без дати - приблизно (~), за останнім уроком.
              </S.Hint>
            </S.Card>
          </>
        )}
      </S.Page>
    </PagesWrapper>
  );
}

// Контакт: скопіювати текст + позначити результат або «пішов» (з причиною зі списку)
function Actions({ s, reasons, onChange, notify }) {
  const [busy, setBusy] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const [reason, setReason] = useState("");

  const mark = (status) => {
    if (!status) return;
    if (status === "left") return setLeaving(true);
    const note = status === "replied" ? window.prompt("Коротко: що відповів (можна пропустити)") || "" : "";
    setBusy(true);
    axios.post(`admin/churn-risk/${s._id}/outreach`, { status, note, risk: s.level, reasons: s.signals.map((x) => x.text) })
      .then(onChange)
      .catch(() => alert("Не вдалося зберегти"))
      .finally(() => setBusy(false));
  };
  const markLeft = () => {
    if (!reason) return;
    setBusy(true);
    axios.put(`student/pause/${s._id}`, { isPaused: true, left: true, leftReason: reason, teacherId: s.teacherId, pauseNote: s.pauseNote || "" })
      .then(() => { setLeaving(false); onChange(); notify(`${firstName(s.name)} - «пішов», нагадаємо написати через 60 днів`); })
      .catch((e) => alert(e?.response?.data?.message || "Не вдалося зберегти"))
      .finally(() => setBusy(false));
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6, alignItems: "flex-start" }}>
      {s.outreach && (
        <span><S.Tag $c={OUTREACH[s.outreach.status]?.[1]}>{OUTREACH[s.outreach.status]?.[0]}</S.Tag> <S.Small as="span">{day(s.outreach.at)}</S.Small>
          {s.outreach.note && <S.Hint>{s.outreach.note}</S.Hint>}</span>
      )}
      {leaving ? (
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          <S.Select value={reason} onChange={(e) => setReason(e.target.value)} aria-label="Причина">
            <option value="" disabled>Чому пішов?</option>
            {Object.entries(reasons).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </S.Select>
          <S.Btn onClick={markLeft} disabled={!reason || busy}>OK</S.Btn>
          <S.Btn $ghost onClick={() => setLeaving(false)}>✕</S.Btn>
        </div>
      ) : (
        <div style={{ display: "flex", gap: 6 }}>
          <S.Btn $ghost onClick={async () => notify((await copy(messageFor(s))) ? `Текст для ${firstName(s.name)} скопійовано` : "Не вдалося скопіювати")}>Текст</S.Btn>
          <S.Select value="" disabled={busy} onChange={(e) => mark(e.target.value)} aria-label="Результат контакту">
            <option value="">Позначити…</option>
            {Object.entries(OUTREACH).map(([k, [label]]) => <option key={k} value={k}>{label}</option>)}
            {s.level !== "left" && <option value="left">пішов…</option>}
          </S.Select>
        </div>
      )}
    </div>
  );
}

function Row({ s, reasons, onChange, notify }) {
  const expected = s.perWeek ? s.perWeek * 4 : null;
  return (
    <tr>
      <td><b>{withId(s.name, s.studentId)}</b><S.Hint>{s.phoneNumber || "—"}</S.Hint></td>
      <td>{withId(s.teacherName, s.teacherCode)}</td>
      <td><S.Tag $c={s.balance <= 0 ? "red" : s.balance <= 2 ? "amber" : undefined}>{s.balance} ур.</S.Tag>
        {s.lastPayment && <S.Hint>оплата {day(s.lastPayment.at)}: {s.lastPayment.lessons} ур.</S.Hint>}</td>
      <td>{s.lastLessonAt ? day(s.lastLessonAt) : "—"}{s.daysSince !== null && <S.Hint>{s.daysSince} дн. тому</S.Hint>}</td>
      <td>{s.last4w}{expected ? ` з ${expected}` : ""}{!s.perWeek && <S.Hint>розклад не заповнено</S.Hint>}</td>
      <td>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
          {s.signals.map((x) => <S.Tag key={x.key} $c={s.level === "red" || s.level === "gone" ? "red" : "amber"}>{x.text}</S.Tag>)}
        </div>
        {s.checkTeacher && <S.Hint style={{ color: "#b45309" }}>Перевір з викладачем: учень пропускає чи уроки не відмічені?</S.Hint>}
      </td>
      <td>{s.level !== "ok" && <Actions s={s} reasons={reasons} onChange={onChange} notify={notify} />}</td>
    </tr>
  );
}

function PausedRow({ s, reasons, onChange, notify }) {
  return (
    <tr>
      <td><b>{withId(s.name, s.studentId)}</b><S.Hint>{s.phoneNumber || "—"}</S.Hint></td>
      <td>{withId(s.teacherName, s.teacherCode)}</td>
      <td>{s.since ? `${s.sinceApprox ? "~" : ""}${day(s.since)}` : "давно (до серпня)"}{s.sinceApprox && s.since && <S.Hint>за останнім уроком</S.Hint>}</td>
      <td>
        {s.leftReasonLabel && <S.Tag $c="red">{s.leftReasonLabel}</S.Tag>}
        {s.pauseNote ? <S.Hint>{s.pauseNote}</S.Hint> : !s.leftReasonLabel && "—"}
      </td>
      <td>{s.due ? <S.Tag $c="amber">пора написати</S.Tag> : day(s.comebackAfter)}</td>
      <td><Actions s={s} reasons={reasons} onChange={onChange} notify={notify} /></td>
    </tr>
  );
}

export default ChurnRisk;
